import { Refine } from '@refinedev/core';
import {
  ThemedLayoutV2,
  notificationProvider,
} from '@refinedev/antd';
import { DataProvider } from '@refinedev/core';
import routerProvider from '@refinedev/react-router-v6';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import ConfigProvider from 'antd/es/config-provider';
import { lazy, Suspense } from 'react';
import { getApiUrl } from './lib/config';

// Lazy load pages
const LobbyList = lazy(() => import('./pages/lobby/list'));
const GameCreate = lazy(() => import('./pages/lobby/create'));
const SinglePlayerCreate = lazy(() => import('./pages/lobby/single-player'));
const GameJoin = lazy(() => import('./pages/lobby/join'));
const BasicPlay = lazy(() => import('./pages/game/basic-play'));

// Components
import AppHeader from './components/AppHeader';
import AppFooter from './components/AppFooter';

// Storage key constants
const PLAYER_ID_KEY = 'tth_player_id';

// Helper to get player ID from localStorage
const getPlayerId = () => localStorage.getItem(PLAYER_ID_KEY);

// Data provider implementation
const dataProvider: DataProvider = {
  create: async ({ resource, variables }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const response = await fetch(`${apiUrl}/${resource}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to create ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  createMany: async ({ resource, variables }) => {
    const results = await Promise.all(
      variables.map((v) => dataProvider.create({ resource, variables: v }) as Promise<{ data: any }>)
    );
    return { data: results.map((r) => r.data) };
  },

  deleteOne: async ({ resource, id }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const response = await fetch(`${apiUrl}/${resource}/${id}`, {
      method: 'DELETE',
      headers: {
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  deleteMany: async ({ resource, ids }) => {
    const results = await Promise.all(
      ids.map((id) => dataProvider.deleteOne({ resource, id }) as Promise<{ data: any }>)
    );
    return { data: results.map((r) => r.data) };
  },

  getList: async ({ resource, filters }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const url = new URL(`${apiUrl}/${resource}`, window.location.origin);

    // Add query params for filtering
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if ('field' in filter && 'operator' in filter && 'value' in filter) {
          url.searchParams.set(filter.field, String(filter.value));
        }
      });
    }

    const response = await fetch(url.toString(), {
      headers: {
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${resource}`);
    }

    const data = await response.json();
    return data;
  },

  getMany: async ({ resource, ids }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const promises = ids.map((id) =>
      fetch(`${apiUrl}/${resource}/${id}`, {
        headers: {
          ...(playerId ? { 'X-Player-ID': playerId } : {}),
        },
      })
    );

    const responses = await Promise.all(promises);
    const data = await Promise.all(responses.map((r) => r.json()));

    return { data };
  },

  getOne: async ({ resource, id }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const response = await fetch(`${apiUrl}/${resource}/${id}`, {
      headers: {
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to get ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  update: async ({ resource, id, variables, meta }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    // For patch operations like /players/:id/game
    const url = meta?.patchEndpoint
      ? `${apiUrl}/${resource}/${id}/${meta.patchEndpoint}`
      : `${apiUrl}/${resource}/${id}`;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
      },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const results = await Promise.all(
      ids.map((id) => dataProvider.update({ resource, id, variables }) as Promise<{ data: any }>)
    );
    return { data: results.map((r) => r.data) };
  },

  custom: async ({ url, method, headers, payload, query, meta }) => {
    const apiUrl = getApiUrl();
    const playerId = getPlayerId();

    const fullUrl = url ?? `${apiUrl}/${meta?.resource ?? ''}`;

    const queryParams = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.set(key, String(value));
        }
      });
    }

    const urlWithQuery = queryParams.toString()
      ? `${fullUrl}?${queryParams.toString()}`
      : fullUrl;

    const response = await fetch(urlWithQuery, {
      method: method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(playerId ? { 'X-Player-ID': playerId } : {}),
        ...headers,
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Request failed');
    }

    const data = await response.json();
    return { data };
  },

  getApiUrl: () => getApiUrl(),
};

// Loading fallback
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
    Loading...
  </div>
);

const App = () => {
  return (
    <BrowserRouter>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1890ff',
            borderRadius: 4,
          },
        }}
      >
        <Refine
            dataProvider={dataProvider}
            routerProvider={routerProvider}
            notificationProvider={notificationProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
            }}
          >
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  element={
                    <ThemedLayoutV2
                      Sider={() => null}
                      Header={() => <AppHeader />}
                      Footer={() => <AppFooter />}
                    >
                      <Outlet />
                    </ThemedLayoutV2>
                  }
                >
                  <Route index element={<Navigate to="/lobby" replace />} />
                  <Route path="/lobby" element={<LobbyList />} />
                  <Route path="/lobby/create" element={<GameCreate />} />
                  <Route path="/single-player" element={<SinglePlayerCreate />} />
                  <Route path="/lobby/join/:gameId" element={<GameJoin />} />
                  <Route path="/lobby/game/:gameId" element={<GameJoin />} />
                  <Route path="/game/basic/:gameId?" element={<BasicPlay />} />
                </Route>
              </Routes>
            </Suspense>
          </Refine>
      </ConfigProvider>
    </BrowserRouter>
  );
};

export default App;
